/**
 * The app-level error boundary (SBX-537 FE14, SCP ErrorBoundary
 * hardening; prod-audit P1-6). It used to render error.toString()
 * plus the full React componentStack to the END USER - a raw stack
 * in front of a client is a dev-language leak and quietly discloses
 * internals. The fallback now speaks a friendly sentence plus a
 * quotable client-minted error id; the REAL error and stack go to
 * the console beside the same id, so a user's quote and a support
 * grep meet (the FE13-3 SurfaceBoundary pattern, platform-wide).
 *
 * Chunk-load failures get their own fallback: when the loop-guarded
 * auto-reload (staleChunkReload) has already spent its attempt, the
 * fallback offers a page refresh - a re-render cannot recover because
 * React.lazy caches a rejected import for the life of the tab.
 */
import React from 'react'
import { T } from '@transifex/react'

// PRO-130: the LEAF seam modules, NOT the `src/antd` barrel — ErrorBoundary is
// boot-static (rendered by App + child-router-factory), so a barrel import
// welds the whole antd component barrel onto the entry chunk.
import { Button } from 'src/antd/Button'
import { Result } from 'src/antd/Result'
import Typography from 'src/antd/Typography'
import { seedChromeCatalog } from 'src/utils/chromeI18n'
import { reportBoundaryError } from 'src/utils/sentry'
import {
  canReloadForStaleChunk,
  isChunkLoadError,
  reloadForStaleChunk,
} from 'src/utils/staleChunkReload'

seedChromeCatalog({
  da: {
    'Oops! Something went wrong': 'Ups! Noget gik galt',
    'This page hit an unexpected problem and stopped. Nothing already saved is affected.':
      'Denne side stødte på en uventet fejl og stoppede. Intet allerede gemt er berørt.',
    'If it keeps happening, quote error id {id} to support.':
      'Sker det igen, så oplys fejl-id {id} til support.',
    'Try again': 'Prøv igen',
    'A new version is available': 'En ny version er tilgængelig',
    'The platform was updated while this tab was open, so part of this page could not load. Nothing already saved is affected.':
      'Platformen blev opdateret, mens denne fane var åben, så en del af siden kunne ikke indlæses. Intet allerede gemt er berørt.',
    'If refreshing does not help, quote error id {id} to support.':
      'Hjælper en opdatering ikke, så oplys fejl-id {id} til support.',
    Refresh: 'Opdater',
  },
})

/** A quotable reference for a support conversation: minted at catch
 * time, rendered in the fallback AND printed beside the console
 * stack, so "the user quotes it" and "support greps for it" meet. */
function newErrorId(): string {
  const stamp = Date.now().toString(36)
  const salt = Math.random().toString(36).slice(2, 6)
  return `WH-${stamp}-${salt}`.toUpperCase()
}

type Props = { children: React.ReactNode }
type State = { errorId: string | null; reloading: boolean; staleChunk: boolean }

export class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { errorId: null, reloading: false, staleChunk: false }
  }

  static getDerivedStateFromError(error: unknown): State {
    // A stale-chunk import failure after a deploy: silently refresh to the
    // freshly-deployed build instead of rendering "Oops" - unless we just
    // reloaded (loop guard tripped). Even then the fallback must offer a
    // RELOAD, not a re-render: React.lazy caches a rejected import forever,
    // so "Try again" can never bring a failed chunk back.
    if (isChunkLoadError(error)) {
      if (canReloadForStaleChunk()) {
        return { errorId: null, reloading: true, staleChunk: false }
      }
      return { errorId: newErrorId(), reloading: false, staleChunk: true }
    }
    return { errorId: newErrorId(), reloading: false, staleChunk: false }
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    const { errorId, reloading } = this.state
    if (reloading) {
      reloadForStaleChunk()
      return
    }

    // The real error and stack, where support can pull them. The
    // error id ties a user's quote to this exact console line.
    // eslint-disable-next-line no-console
    console.error(
      `ErrorBoundary caught (error id ${errorId}):`,
      error,
      info.componentStack
    )
    // Same error id, componentStack, and error travel to Sentry so the user's
    // quote, the console line, and the Sentry issue all reference one event.
    reportBoundaryError(error, info.componentStack, errorId)
  }

  render() {
    const { errorId, reloading, staleChunk } = this.state
    const { children } = this.props

    // Blank while the tab refreshes to the new build - avoids flashing "Oops".
    if (reloading) return null

    // The loop-guarded stale-chunk case: the auto-reload already ran (or was
    // refused) moments ago, so hand the reload to the user honestly instead
    // of blaming the page. The error id stays quotable - if refreshing does
    // not recover, the build itself is broken and support needs the trace.
    if (errorId && staleChunk) {
      return (
        <div
          style={{
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Result
            status="info"
            title={<T _str="A new version is available" />}
            subTitle={
              <Typography.Paragraph
                style={{ textAlign: 'center', maxWidth: 600 }}
              >
                <T _str="The platform was updated while this tab was open, so part of this page could not load. Nothing already saved is affected." />{' '}
                <span data-app-error-id={errorId}>
                  <T
                    _str="If refreshing does not help, quote error id {id} to support."
                    id={errorId}
                  />
                </span>
              </Typography.Paragraph>
            }
            extra={
              <Button onClick={() => window.location.reload()} type="primary">
                <T _str="Refresh" />
              </Button>
            }
          />
        </div>
      )
    }

    if (errorId) {
      return (
        <div
          style={{
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Result
            status="warning"
            title={<T _str="Oops! Something went wrong" />}
            subTitle={
              <Typography.Paragraph
                style={{ textAlign: 'center', maxWidth: 600 }}
              >
                <T _str="This page hit an unexpected problem and stopped. Nothing already saved is affected." />{' '}
                <span data-app-error-id={errorId}>
                  <T
                    _str="If it keeps happening, quote error id {id} to support."
                    id={errorId}
                  />
                </span>
              </Typography.Paragraph>
            }
            extra={
              <Button
                onClick={() =>
                  this.setState({ errorId: null, reloading: false })
                }
                type="primary"
              >
                <T _str="Try again" />
              </Button>
            }
          />
        </div>
      )
    }

    return children
  }
}
